import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { APP_PATH } from "../../api/backendPath";

const getAuthHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("usr_1a2b3c")}`,
});

export const fetchImagingOrders = createAsyncThunk(
  "imagingOrders/fetchImagingOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search, date, status } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search && search.trim()) queryParams.append("search", search.trim());
      if (date) queryParams.append("date", date);
      if (status && status !== "All") queryParams.append("status", status);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}?${queryParams.toString()}`,
        { headers: getAuthHeader() },
      );

      const responseData = response.data?.data;
      return {
        data: responseData?.orders || [],
        pagination: {
          currentPage: responseData?.pagination?.page || 1,
          totalPages: responseData?.pagination?.totalPages || 1,
          totalItems: responseData?.pagination?.total || 0,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch imaging orders",
      );
    }
  },
);

export const fetchImagingOrderDetails = createAsyncThunk(
  "imagingOrders/fetchOrderDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingOrders}/${orderId}`,
        { headers: getAuthHeader() },
      );
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch order details",
      );
    }
  },
);

export const fetchImagingServices = createAsyncThunk(
  "imagingOrders/fetchServices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingServices}?limit=100&status=ACTIVE`,
        { headers: getAuthHeader() },
      );
      return response.data?.data?.services || [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch imaging services",
      );
    }
  },
);

export const fetchPendingOrders = createAsyncThunk(
  "imagingOrders/fetchPendingOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { tab, search, page = 1, limit = 8 } = params;
      const q = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (tab) q.append("tab", tab);
      if (search) q.append("search", search);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingOrdersPending}?${q.toString()}`,
        { headers: getAuthHeader() },
      );
      const d = response.data?.data;
      return { orders: d?.orders || [], pagination: d?.pagination || {} };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch pending orders",
      );
    }
  },
);

export const fetchOngoingOrders = createAsyncThunk(
  "imagingOrders/fetchOngoingOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { tab, search, page = 1, limit = 8 } = params;
      const q = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (tab) q.append("tab", tab);
      if (search) q.append("search", search);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingOrdersOngoing}?${q.toString()}`,
        { headers: getAuthHeader() },
      );
      const d = response.data?.data;
      return { orders: d?.orders || [], pagination: d?.pagination || {} };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch ongoing orders",
      );
    }
  },
);

export const fetchDashboardMetrics = createAsyncThunk(
  "imagingOrders/fetchDashboardMetrics",
  async (params = {}, { rejectWithValue }) => {
    try {
      const orderType = params?.insured ? "insured" : "normal";
      const q = new URLSearchParams({ order_type: orderType });
      if (params?.date_from) q.append("date_from", params.date_from);
      if (params?.date_to) q.append("date_to", params.date_to);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingOrdersMetrics}?${q.toString()}`,
        { headers: getAuthHeader() },
      );
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch dashboard metrics",
      );
    }
  },
);

export const fetchImagingReports = createAsyncThunk(
  "imagingOrders/fetchImagingReports",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { search, reportStatus, date, page = 1, limit = 10 } = params;
      const q = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) q.append("search", search);
      if (reportStatus && reportStatus !== "All")
        q.append("reportStatus", reportStatus);
      if (date) q.append("date", date);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getImagingReports}?${q.toString()}`,
        { headers: getAuthHeader() },
      );
      const d = response.data?.data;
      return { reports: d?.reports || [], pagination: d?.pagination || {} };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch imaging reports",
      );
    }
  },
);

const imagingOrdersSlice = createSlice({
  name: "imagingOrders",
  initialState: {
    data: [],
    orderDetails: null,
    imagingServices: [],
    pendingOrders: [],
    pendingPagination: {},
    ongoingOrders: [],
    ongoingPagination: {},
    dashboardMetrics: null,
    // requestId of the latest fetchDashboardMetrics (see the reducer guard below).
    metricsRequestId: null,
    imagingReports: [],
    reportsPagination: {},
    isReportsLoading: false,
    isLoading: false,
    isDetailsLoading: false,
    isServicesLoading: false,
    isPendingLoading: false,
    isOngoingLoading: false,
    isMetricsLoading: false,
    error: null,
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  },
  reducers: {
    clearImagingOrders: (state) => {
      state.data = [];
      state.error = null;
    },
    clearOrderDetails: (state) => {
      state.orderDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchImagingOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchImagingOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload?.data || [];
        state.currentPage = action.payload?.pagination?.currentPage || 1;
        state.totalPages = action.payload?.pagination?.totalPages || 1;
        state.totalItems = action.payload?.pagination?.totalItems || 0;
      })
      .addCase(fetchImagingOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchImagingOrderDetails.pending, (state) => {
        state.isDetailsLoading = true;
        state.error = null;
      })
      .addCase(fetchImagingOrderDetails.fulfilled, (state, action) => {
        state.isDetailsLoading = false;
        state.orderDetails = action.payload;
      })
      .addCase(fetchImagingOrderDetails.rejected, (state, action) => {
        state.isDetailsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchImagingServices.pending, (state) => {
        state.isServicesLoading = true;
      })
      .addCase(fetchImagingServices.fulfilled, (state, action) => {
        state.isServicesLoading = false;
        state.imagingServices = action.payload;
      })
      .addCase(fetchImagingServices.rejected, (state) => {
        state.isServicesLoading = false;
      })
      .addCase(fetchPendingOrders.pending, (state) => {
        state.isPendingLoading = true;
      })
      .addCase(fetchPendingOrders.fulfilled, (state, action) => {
        state.isPendingLoading = false;
        state.pendingOrders = action.payload.orders;
        state.pendingPagination = action.payload.pagination;
      })
      .addCase(fetchPendingOrders.rejected, (state) => {
        state.isPendingLoading = false;
      })
      .addCase(fetchOngoingOrders.pending, (state) => {
        state.isOngoingLoading = true;
      })
      .addCase(fetchOngoingOrders.fulfilled, (state, action) => {
        state.isOngoingLoading = false;
        state.ongoingOrders = action.payload.orders;
        state.ongoingPagination = action.payload.pagination;
      })
      .addCase(fetchOngoingOrders.rejected, (state) => {
        state.isOngoingLoading = false;
      })
      // Only the most recent metrics request may write the result. The Dashboard (all-time)
      // and Earnings & Payments (date range / insurance toggle) share this slot, and picking
      // From then To fires two requests — without this guard a slow earlier response could
      // overwrite the figures for the filter currently selected.
      .addCase(fetchDashboardMetrics.pending, (state, action) => {
        state.metricsRequestId = action.meta.requestId;
        state.isMetricsLoading = true;
      })
      .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.metricsRequestId) return;
        state.isMetricsLoading = false;
        state.dashboardMetrics = action.payload;
      })
      .addCase(fetchDashboardMetrics.rejected, (state, action) => {
        if (action.meta.requestId !== state.metricsRequestId) return;
        state.isMetricsLoading = false;
        // Don't leave the previous filter's figures on screen as if they matched this one.
        state.dashboardMetrics = null;
      })
      .addCase(fetchImagingReports.pending, (state) => {
        state.isReportsLoading = true;
      })
      .addCase(fetchImagingReports.fulfilled, (state, action) => {
        state.isReportsLoading = false;
        state.imagingReports = action.payload.reports;
        state.reportsPagination = action.payload.pagination;
      })
      .addCase(fetchImagingReports.rejected, (state) => {
        state.isReportsLoading = false;
      });
  },
});

export const { clearImagingOrders, clearOrderDetails } =
  imagingOrdersSlice.actions;
export default imagingOrdersSlice.reducer;
